import { useCallback } from "react";
import { Validation } from "@madhive/mad-sdk";
import { useSingularMadHooksUtils, getSingularDataObservable } from "./singularUtils";
import { useValidation, getSingularKeysToValidate, getSingularValidations } from "./useValidation";
import { getHandler } from "../../../utils";
/**
 * Returns a function that will validate and update the singular with the provided data.
 * @param collection Collection the data belongs to
 * @param id The id of the data
 * @returns a Promise
 */
export const useChange = (collection, id) => {
    const { observer, handler } = useSingularMadHooksUtils(collection, id);
    const { validateFunction } = useValidation(handler, observer);
    const changeFunction = useCallback((changes, shouldValidate = true) => {
        changeSingularData(collection, id, changes, shouldValidate);
    }, [id, collection]);
    return { changeFunction, validateFunction };
};
/**
 * Standalone (non-hook) function that applies delta changes to a singular data entry.
 */
export function changeSingularData(collection, entityId, changes, shouldValidate = true) {
    const id = String(entityId);
    const observer = getSingularDataObservable(collection, id);
    const handler = getHandler(collection);
    const validationErrors = observer.value.validationErrors || new Validation();
    const validations = [];
    // Note: Spread operator avoided here because pendingChanges proxy is pointing to this deltas.
    Object.assign(observer.value.deltas, changes);
    const keysToValidate = getSingularKeysToValidate(collection, id, { changes });
    observer.next({
        ...observer.value,
        isValidating: keysToValidate
    });
    keysToValidate.forEach((key) => {
        const value = changes[key];
        // Remove delta if setting back to original value, but not if it was not originally defined because
        // we want to validate in case an empty state is not allowed.
        if (observer.value?.data &&
            typeof observer.value?.data === "object" &&
            key in observer.value.data &&
            observer.value.data?.[key] === value) {
            delete observer.value.deltas[key];
            if (validationErrors?.has(key)) {
                validationErrors.delete(key);
            }
            return;
        }
        if (shouldValidate) {
            validations.push(new Promise((resolve) => {
                const validate = handler.validate(observer.value.pendingChanges, key, value);
                const keyString = key.toString();
                getSingularValidations(validate, keyString).then((validation) => {
                    if (validation) {
                        const val = validation.get(keyString);
                        val && validationErrors.set(keyString, val);
                    }
                    else {
                        validationErrors.delete(key);
                    }
                    resolve();
                });
            }));
        }
    });
    // As of right now we want to accept the change to deltas even if
    // validation happens to fail since we don't want to effect the users
    // input
    observer.next({
        ...observer.value,
        _changeVersion: (observer.value._changeVersion ?? 0) + 1
    });
    Promise.all(validations).finally(() => {
        observer.next({
            ...observer.value,
            validationErrors,
            isValidating: false,
            _changeVersion: (observer.value._changeVersion ?? 0) + 1
        });
    });
}
//# sourceMappingURL=useChange.js.map